<template>
	<div v-if="this.treeData !== null">
		<div className="form-group">
			<label v-if="label != null">{{ label }}</label>

			<treeselect
				ref="treeselect"
				:instanceId="instanceId"
				:options="treeData"
				:multiple="multiple"
				:async="async"
				:load-options="loadOptions"
				:default-options="defaultOptions"
				:placeholder="placeholder"
				:value-consists-of="valueConsistsOf"
				:normalizer="normalizer"
				:show-count="true"
				:disabled="disabled"
				:flatten-search-results="true"
				:value="serializedValue"
				v-on:input="emitChanges"
				v-on:select="emitItemSelection"
				v-on:deselect="emitItemDeselection" />
		</div>

		<template v-if="serializedValue !== null && multiple === true">
			<input v-for="itm in serializedValue" type="hidden" :name="`${inputName}[]`" :value="itm" />
		</template>
		<template v-else>
			<input type="hidden" :name="inputName" :value="serializedValue" />
		</template>
	</div>
</template>

<script>
	// Treeselect module from github https://github.com/riophae/vue-treeselect
	import Treeselect from '@riophae/vue-treeselect';
	import '@riophae/vue-treeselect/dist/vue-treeselect.css';
	import { map } from 'lodash';

	/**
	 * This module require the npm packages: list-to-tree, liquor-tree
	 */
	export default {
		name: 'GTreeView',

		components: {
			Treeselect,
		},

		props: {
			instanceId: {
				type: [String, Number],
				default: 1,
			},
			inputName: String,
			label: {
				type: String,
				default: null,
			},
			idProp: {
				type: String,
				default: '@id',
			},
			labelProp: {
				type: String,
				default: 'name',
			},
			placeholder: String,
			defaultOptions: [Array, Boolean],
			fetchUrl: String,
			fetchIdKey: String,
			fetchParentKey: String,
			localData: Array,
			async: Boolean,
			loadOptions: Function,
			disabled: {
				type: Boolean,
				default: false,
			},
			multiple: {
				type: Boolean,
				default: true,
			},
			value: {
				// given by v-model automatically
				default: null,
			},
			valueConsistsOf: {
				default: 'ALL_WITH_INDETERMINATE',
			},
		},

		data() {
			return {
				treeData: null,
				valueData: null,
			};
		},

		watch: {
			value: {
				immediate: true,
				handler: function (newVal) {
					this.valueData = newVal;
				},
			},

			localData: function (newValue) {
				this.fetchData();
			},
		},

		computed: {
			serializedValue() {
				if (typeof this.value === 'string' || typeof this.value === 'number' || this.value == null) {
					return this.valueData;
				} else {
					return map(this.value, function (item) {
						if (item['@id'] != null) {
							return item['@id'];
						} else {
							return item;
						}
					});
				}
			},
		},

		async mounted() {
			await this.fetchData();

			// disable autocomplete of chrome
			if (this.$refs.treeselect && this.$refs.treeselect.$el.querySelector('input')) {
				this.$refs.treeselect.$el.querySelector('input').setAttribute('autocomplete', 'new-password');
			}
		},

		methods: {
			emitChanges(data) {
				this.valueData = data;
				this.$emit('input', data);
			},

			emitItemSelection(data) {
				this.valueData = data['@id'];
				this.$emit('update:selected', data['@id']);
			},

			emitItemDeselection(data) {
				this.$emit('deselect', data);
			},

			fetchData() {
				if (this.fetchUrl) {
					this.$axios.get(this.fetchUrl).then(res => {
						if (this.fetchParentKey && this.fetchIdKey) {
							this.treeData = this.jsonToTree(
								res.data['hydra:member'],
								this.fetchIdKey,
								this.fetchParentKey
							);
						} else {
							this.treeData = res.data['hydra:member'];
						}
					});
				} else {
					if (this.fetchParentKey && this.fetchIdKey) {
						this.treeData = this.jsonToTree(this.localData, this.fetchIdKey, this.fetchParentKey);
					} else {
						this.treeData = this.localData;
					}
				}
			},

			/**
			 * If your data of options is loaded via AJAX and have a different data structure than what we asks,
			 * e.g. your data has 'name' property but we needs 'label', you may want to customize the key names.
			 */
			normalizer(node) {
				return {
					id: node[this.idProp],
					label: node[this.labelProp],
					children: node.child,
				};
			},

			/**
			 * Transform JSON to Tree (array) with parent and childs.
			 */
			jsonToTree(data, idKey, parentKey) {
				let LTT = require('list-to-tree');

				let obj = new LTT(data, {
					key_id: idKey,
					key_parent: parentKey,
				});

				return obj.GetTree();
			},
		},
	};
</script>

<style>
	.vue-treeselect__label,
	.vue-treeselect__value-container,
	.vue-treeselect__placeholder {
		font-size: 12px;
		text-transform: uppercase;
	}
	.vue-treeselect__control {
		width: 190px;
	}
	.vue-treeselect__placeholder {
		text-transform: capitalize;
	}
</style>
